import { Button, Card, DatePicker, Divider, Flex, Space, Spin } from "antd";
import { getFaxJobSummary } from "api/services/FaxJobSummaryService";
import { FaxJobStatus, TFaxJobSummary } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import ErrorViewer from "components/ErrorViewer";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";
import FaxJobSummaryGrid from "./FaxJobSummaryGrid";
import { ReactComponent as CalendarIcon } from "../../Icons/vuesax/calendar.svg";
import { ReactComponent as SimCardIcon } from "../../Icons/vuesax/simcard.svg";
import { ReactComponent as VerifiedIcon } from "../../Icons/vuesax/verify.svg";
import { ReactComponent as TimerIcon } from "../../Icons/vuesax/timer.svg";
import { ReactComponent as TickCircleicon } from "../../Icons/vuesax/tick-circle.svg";
import { ReactComponent as CloseCircleIcon } from "../../Icons/vuesax/close-circle.svg";
import { ReactComponent as HamburgerIcon } from "../../Icons/vuesax/menu.svg";
import { useLayoutContext } from "components/MainContainer";
import SvgOverlay from "components/SvgOverlay";
import { ReactComponent as ArrowLeftIcon } from "../../Icons/vuesax/arrow-left.svg";
import { ReactComponent as ArrowRigthIcon } from "../../Icons/vuesax/arrow-right.svg";

const FaxJobSummary = () => {
  const { accountId } = useTDAuthentication();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [faxSummaryDate, setFaxSummaryDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [faxSummaryData, setFaxSummaryData] = useState<TFaxJobSummary[]>();
  const minDate = dayjs().startOf("day").subtract(90, "day");
  const maxDate = dayjs().startOf("day");
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const [totals, setTotals] = useState<Record<string, number>>({
    numberOfJobs: 0,
    ready: 0,
    inProgress: 0,
    completed: 0,
    rejected: 0
  });

  useEffect(() => {
    if (accountId) {
      setLoading(true);
      setError(null);
      getFaxJobSummary(accountId, faxSummaryDate)
        .then(data => {setFaxSummaryData(data);
          const calculatedTotals = data.reduce((op, summary) => {
              op.numberOfJobs= (op.numberOfJobs ?? 0) + 1;
              op.ready= (op.ready ?? 0) + isReady(summary);
              op.inProgress= (op.inProgress ?? 0) + inProgress(summary);
              op.completed= (op.completed ?? 0) + isCompleted(summary);
              op.rejected= (op.rejected ?? 0) + isRejected(summary);
            return op;
          }, {} as Record<string, number>);
          setTotals(calculatedTotals);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [faxSummaryDate, accountId]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);
  
  const onChange = useCallback(
    (newSummaryDate: Dayjs) => setFaxSummaryDate(newSummaryDate),
    []
  );

  const isReady = (summary: TFaxJobSummary) => (summary.status === FaxJobStatus.New ? 1 : 0);
  const inProgress = (summary: TFaxJobSummary) =>
    summary.status === FaxJobStatus.Inprogress ||
    summary.status === FaxJobStatus.Spooled ||
    summary.status === FaxJobStatus.Queued ||
    (summary.status === FaxJobStatus.Error && summary.numRetries < 3)
      ? 1
      : 0;
  const isCompleted = (summary: TFaxJobSummary) =>
    summary.status === FaxJobStatus.Completed ? 1 : 0;
  const isRejected = (summary: TFaxJobSummary) =>
    summary.status === FaxJobStatus.Error && summary.numRetries >= 3 ? 1 : 0;

  
  const getFormattedDate = (date: Dayjs | null) => {
    if (date) {
      return date.format("dddd, D MMMM"); 
    }
    return "Select a date";
  };

  
const [datePickerOpen, setDatePickerOpen] = useState(false);


  return (
    <>
      {!isSmallScreen ? (
        <div className="container">
          <div className="header">
            <div className="header-title">
              {isSmallScreen && (
                <Button
                  type="text"
                  icon={<HamburgerIcon />}
                  onClick={toggleDrawer}
                  className="hamburger-menu-button"
                  style={{ marginRight: ".25rem" }}
                />
              )}
              <span>Fax Summary</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <span
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    marginRight: "0.5rem"
                  }}>
                  Date:
                </span>
                <DatePicker
                  value={faxSummaryDate}
                  className="custom-datepicker"
                  maxDate={maxDate}
                  minDate={minDate}
                  allowClear={false}
                  onChange={onChange}
                  suffixIcon={<CalendarIcon className="calendar-icon" />}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          <div className="card-container">
            <Card className="card " styles={{ body: { padding: "1rem" } }}>
              <div className="card-content">
                <SvgOverlay
                  color="#FFB876"
                  overlaySvg={<SimCardIcon width="1.25rem" height="1.25rem" />}
                />
                <div className="card-text">
                  <span className="font-777" style={{ marginBottom: ".25rem" }}>
                    Total Fax Jobs
                  </span>
                  <span className="font-1212" style={{ marginBottom: "1rem" }}>
                    {totals.numberOfJobs ?? 0}
                  </span>
                </div>
              </div>
            </Card>
            <Card className="card " styles={{ body: { padding: "1rem" } }}>
              <div className="card-content">
                <SvgOverlay
                  color="#76D6FF"
                  overlaySvg={<VerifiedIcon width="1.25rem" height="1.25rem" />}
                />
                <div className="card-text">
                  <span className="font-777" style={{ marginBottom: ".25rem" }}>
                    Ready
                  </span>
                  <span className="font-1212" style={{ marginBottom: "1rem" }}>
                    {totals.ready ?? 0}
                  </span>
                </div>
              </div>
            </Card>
            <Card className="card " styles={{ body: { padding: "1rem" } }}>
              <div className="card-content">
                <SvgOverlay
                  color="#FFE176"
                  overlaySvg={<TimerIcon width="1.25rem" height="1.25rem" />}
                />
                <div className="card-text">
                  <span className="font-777" style={{ marginBottom: ".25rem" }}>
                    In Progress
                  </span>
                  <span className="font-1212" style={{ marginBottom: "1rem" }}>
                    {totals.inProgress ?? 0}
                  </span>
                </div>
              </div>
            </Card>
            <Card className="card " styles={{ body: { padding: "1rem" } }}>
              <div className="card-content">
                <SvgOverlay
                  color="#9AFF76"
                  overlaySvg={<TickCircleicon width="1.25rem" height="1.25rem" />}
                />
                <div className="card-text">
                  <span className="font-777" style={{ marginBottom: ".25rem" }}>
                    Completed
                  </span>
                  <span className="font-1212" style={{ marginBottom: "1rem" }}>
                    {totals.completed ?? 0}
                  </span>
                </div>
              </div>
            </Card>
            <Card className=" card " styles={{ body: { padding: "1rem" } }}>
              <div className="card-content">
                <SvgOverlay
                  color="#FF9797"
                  overlaySvg={<CloseCircleIcon width="1.25rem" height="1.25rem" />}
                />
                <div className="card-text">
                  <span className="font-777" style={{ marginBottom: ".25rem" }}>
                    Rejected
                  </span>
                  <span className="font-1212" style={{ marginBottom: "1rem" }}>
                    {totals.rejected ?? 0}
                  </span>
                </div>
              </div>
            </Card>
          </div>
          <Card
            className="card-style"
            styles={{ body: { padding: "0px" } }}
            // title={
            //   <FaxJobSummarySearch
            //     faxSummaryDate={faxSummaryDate}
            //     onChange={onChange}
            //     loading={loading}
            //   />
            // }
          >
            {loading && (
              <Flex
                justify="space-around"
                style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
                <Spin tip="Loading, please wait..." />
              </Flex>
            )}
            {error && <ErrorViewer error={error} />}
            {!loading && faxSummaryData && <FaxJobSummaryGrid data={faxSummaryData} />}
          </Card>
        </div>
      ) : (
        <div className="container">
          <div className="header">
            <div className="header-title">
              <Button
                type="text"
                icon={<HamburgerIcon />}
                onClick={toggleDrawer}
                className="hamburger-menu-button"
                style={{ marginRight: ".25rem" }}
              />
              <span>Fax Summary</span>
            </div>
          </div>
          <div
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Button
              type="text"
              icon={
                <ArrowLeftIcon
                  width={"1.5rem"}
                  height={"1.5rem"}
                  style={{ color: "#1868db" }}
                />
              }
              style={{ marginRight: "10px", padding: 0 }}
              onClick={() => {
                if (faxSummaryDate) {
                  setFaxSummaryDate(faxSummaryDate.subtract(1, "day"));
                }
              }}
            />
            <Space
              direction="horizontal"
              size={12}
              onClick={() => setDatePickerOpen(true)}
              style={{ textAlign: "center" }}>
              <div style={{ fontSize: "1rem", fontWeight: "500" }} className="font-1212">
                <div
                  style={{
                    display: "Flex",
                    justifyContent: "flex-start",
                    alignItems: "center"
                  }}>
                  <CalendarIcon
                    className="calendar-icon"
                    style={{ color: "#1868db", marginRight: ".37rem" }}
                  />{" "}
                  {getFormattedDate(faxSummaryDate)}
                </div>
                <DatePicker
                  placement="bottomLeft"
                  value={faxSummaryDate}
                  maxDate={maxDate}
                  minDate={minDate}
                  allowClear={false}
                  onChange={onChange}
                  disabled={loading}
                  format="dddd, D MMMM"
                  style={{ visibility: "hidden", position: "absolute" }}
                  popupStyle={{ zIndex: 9999 }}
                  open={datePickerOpen}
                  onOpenChange={setDatePickerOpen}
                />
              </div>
            </Space>

            <Button
              type="text"
              icon={
                <ArrowRigthIcon
                  width={"1.5rem"}
                  height={"1.5rem"}
                  style={{ color: "#1868db" }}
                />
              }
              style={{ marginLeft: "10px", padding: 0 }}
              onClick={() => {
                if (faxSummaryDate) {
                  setFaxSummaryDate(faxSummaryDate.add(1, "day"));
                }
              }}
            />
          </div>
          <Divider />
          <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <div
              style={{
                flexGrow: 1,
                overflowY: "auto",
                backgroundColor: "#FAFAFA",
                marginBottom: "3.2rem"
              }}>
              <div className="card-container-mobile">
                <Card className="card-mobile" styles={{ body: { padding: ".75rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FFB876"
                      overlaySvg={<SimCardIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Total Fax Jobs
                      </span>
                      <span className="font-1212" style={{ marginBottom: "1rem" }}>
                        {totals.numberOfJobs ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card className="card-mobile" styles={{ body: { padding: "1rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#76D6FF"
                      overlaySvg={<VerifiedIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Ready
                      </span>
                      <span className="font-1212" style={{ marginBottom: "1rem" }}>
                        {totals.ready ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card className="card-mobile" styles={{ body: { padding: "1rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FFE176"
                      overlaySvg={<TimerIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        In Progress
                      </span>
                      <span className="font-1212" style={{ marginBottom: "1rem" }}>
                        {totals.inProgress ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card className="card-mobile" styles={{ body: { padding: "1rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#9AFF76"
                      overlaySvg={<TickCircleicon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Completed
                      </span>
                      <span className="font-1212" style={{ marginBottom: "1rem" }}>
                        {totals.completed ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
                <Card className="card-mobile" styles={{ body: { padding: "1rem" } }}>
                  <div className="card-content-mobile">
                    <SvgOverlay
                      color="#FF9797"
                      overlaySvg={<CloseCircleIcon width="1.25rem" height="1.25rem" />}
                    />
                    <div className="card-text">
                      <span className="font-777" style={{ marginBottom: ".25rem" }}>
                        Rejected
                      </span>
                      <span className="font-1212" style={{ marginBottom: "1rem" }}>
                        {totals.rejected ?? 0}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
              <Divider />
              {loading && (
                <Flex
                  justify="space-around"
                  style={{ width: "100%", textAlign: "center", padding: "1rem" }}>
                  <Spin tip="Loading, please wait..." />
                </Flex>
              )}
              {error && <ErrorViewer error={error} />}
              {!loading && faxSummaryData && <FaxJobSummaryGrid data={faxSummaryData} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FaxJobSummary;
